import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LoginInputSchema, type LoginInput } from '@repo/shared';
import { Banner, FormTextField, Icon, Spinner } from '@repo/ui';
import { useTenant } from '../../app/tenant';
import { homePathFor, useLogin } from '../../auth/session';
import { api, isApiError } from '../../lib/api';
import { AuthLayout, useIsMobile, useIsPhone } from './AuthLayout';

export function PasswordAdornment({
  show,
  onToggle,
  size,
}: {
  show: boolean;
  onToggle: () => void;
  size: 40 | 48;
}) {
  const { t } = useTranslation();
  return (
    <InputAdornment position="end">
      <IconButton
        onClick={onToggle}
        aria-label={show ? t('auth.hidePassword') : t('auth.showPassword')}
        sx={{ width: size, height: size, marginInlineEnd: '-8px' }}
      >
        <Icon name={show ? 'visibility_off' : 'visibility'} size={size === 48 ? 24 : 22} />
      </IconButton>
    </InputAdornment>
  );
}

export function LoginPage() {
  const { t } = useTranslation();
  const tenant = useTenant();
  const mobile = useIsMobile();
  const phone = useIsPhone();
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const login = useLogin();
  const [show, setShow] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'login' | 'forgot'>('login');
  const platform = tenant.kind === 'platform';

  const form = useForm<LoginInput>({
    resolver: zodResolver(LoginInputSchema),
    // Shared computers in schools: a session ends with the browser unless the user asks to stay signed in.
    defaultValues: { identifier: '', password: '', remember: false },
  });
  const forgot = useMutation({
    mutationFn: (identifier: string) =>
      api('/auth/forgot-password', { method: 'POST', body: { identifier } }),
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    setInfo(null);
    try {
      const me = await login.mutateAsync(values);
      const redirect =
        typeof search.redirect === 'string' &&
        search.redirect.startsWith('/') &&
        !search.redirect.startsWith('//')
          ? search.redirect
          : null;
      await navigate({ to: redirect ?? homePathFor(me), replace: true });
    } catch (e) {
      setError(
        isApiError(e, 'INVALID_CREDENTIALS')
          ? t('auth.errors.invalidCredentials')
          : isApiError(e, 'ACCOUNT_LOCKED')
            ? t('auth.errors.locked')
            : isApiError(e, 'RATE_LIMITED')
              ? t('auth.errors.rateLimited')
              : t('auth.errors.generic'),
      );
    }
  });

  const onForgot = () => {
    setError(null);
    setInfo(null);
    forgot.reset();
    setMode('forgot');
  };

  const h = mobile ? 56 : 48;
  const btn = mobile ? { height: 52, fontSize: 16 } : { height: 48, fontSize: 15 };
  const idError = form.formState.errors.identifier?.message;
  const pwError = form.formState.errors.password?.message;

  if (mode === 'forgot')
    return (
      <ForgotPassword
        initial={form.getValues('identifier')}
        platform={platform}
        sending={forgot.isPending}
        sentTo={forgot.isSuccess ? forgot.variables : null}
        onSend={(identifier) => forgot.mutate(identifier)}
        onBack={() => setMode('login')}
      />
    );

  return (
    <AuthLayout>
      <Box
        component="form"
        noValidate
        onSubmit={onSubmit}
        sx={{
          width: '100%',
          maxWidth: mobile ? 'none' : 400,
          display: 'flex',
          flexDirection: 'column',
          gap: mobile ? 2.25 : 2.75,
          flex: phone ? 1 : undefined,
        }}
      >
        {!phone && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography variant="h3" component="h1">
              {t('auth.title')}
            </Typography>
            <Typography sx={{ color: 'text.secondary' }}>
              {t(platform ? 'auth.platform.subtitle' : 'auth.subtitle')}
            </Typography>
          </Box>
        )}
        {info && !mobile && (
          <Banner tone="primary" icon="mail">
            {info}
          </Banner>
        )}
        {error && <Banner tone="err">{error}</Banner>}

        <FormTextField
          label={t(platform ? 'auth.platform.identifier' : 'auth.identifier')}
          placeholder={t(platform ? 'auth.platform.identifierPlaceholder' : 'auth.identifierPlaceholder')}
          autoComplete="username"
          autoFocus={!mobile}
          height={h}
          error={idError ? t(idError) : undefined}
          {...form.register('identifier', { onChange: () => setError(null) })}
        />
        <FormTextField
          label={t('auth.password')}
          type={show ? 'text' : 'password'}
          autoComplete="current-password"
          height={h}
          error={pwError ? t(pwError) : undefined}
          endAdornment={
            <PasswordAdornment show={show} onToggle={() => setShow((s) => !s)} size={mobile ? 48 : 40} />
          }
          {...form.register('password')}
        />

        {/* Same choices on every screen size: the phone layout used to drop « Rester connecté ». */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Controller
            control={form.control}
            name="remember"
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value ?? false}
                    onChange={(e) => field.onChange(e.target.checked)}
                    sx={{ p: mobile ? 1.25 : 0.5, marginInlineEnd: 0.5 }}
                  />
                }
                label={t('auth.remember')}
                sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: mobile ? 15 : 14 } }}
              />
            )}
          />
          <Button
            variant="text"
            onClick={onForgot}
            sx={mobile ? { minHeight: 44, fontSize: 15, px: 1 } : { height: 'auto', px: 0.5, py: 0.5 }}
          >
            {t('auth.forgot')}
          </Button>
        </Box>
        {info && mobile && (
          <Banner tone="primary" icon="mail">
            {info}
          </Banner>
        )}

        <Button
          type="submit"
          disabled={login.isPending}
          startIcon={login.isPending ? <Spinner /> : undefined}
          sx={{ ...btn, gap: 1.25 }}
        >
          {login.isPending ? t('auth.submitting') : t('auth.submit')}
        </Button>

        {tenant.features.sso && (
          <>
            {!mobile && (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'text.tertiary', fontSize: 13 }}
              >
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                {t('common.or')}
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
              </Box>
            )}
            <Button
              variant="outlined"
              onClick={() => setInfo(t('auth.ssoInfo'))}
              startIcon={<Icon name="key" sx={{ color: 'primary.main' }} />}
              sx={{ ...btn, fontSize: mobile ? 15 : 14, color: 'text.primary' }}
            >
              {mobile ? t('auth.ssoShort') : t('auth.sso')}
            </Button>
          </>
        )}

        {!platform && (
          // Design review: the pre-registration entry sat at the bottom of the screen, far from the form.
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: mobile ? 0.5 : 0 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'text.tertiary', fontSize: 13 }}
            >
              <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
              {t('auth.newCandidate')}
              <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
            </Box>
            <Button
              variant="outlined"
              component={Link}
              to="/preinscription"
              startIcon={<Icon name="app_registration" size={20} />}
              sx={{ ...btn, fontSize: mobile ? 15 : 14 }}
            >
              {t('auth.applyLink')}
            </Button>
          </Box>
        )}
      </Box>
    </AuthLayout>
  );
}

/** « Mot de passe oublié » as its own step: one field, one action, the same answer whether the account exists. */
function ForgotPassword({
  initial,
  platform,
  sending,
  sentTo,
  onSend,
  onBack,
}: {
  initial: string;
  platform: boolean;
  sending: boolean;
  sentTo: string | null;
  onSend: (identifier: string) => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const mobile = useIsMobile();
  const [identifier, setIdentifier] = useState(initial.trim());
  const [missing, setMissing] = useState(false);
  const h = mobile ? 56 : 48;
  return (
    <AuthLayout>
      <Box
        component="form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          const value = identifier.trim();
          setMissing(!value);
          if (value) onSend(value);
        }}
        sx={{
          width: '100%',
          maxWidth: mobile ? 'none' : 400,
          display: 'flex',
          flexDirection: 'column',
          gap: mobile ? 2.25 : 2.75,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Typography
            variant="h3"
            component="h1"
            sx={mobile ? { fontSize: 22, lineHeight: '28px' } : undefined}
          >
            {t('auth.forgotTitle')}
          </Typography>
          <Typography sx={{ color: 'text.secondary' }}>{t('auth.forgotText')}</Typography>
        </Box>
        {sentTo ? (
          <Banner tone="primary" icon="mail">
            {t('auth.forgotSent', { identifier: sentTo })}
          </Banner>
        ) : (
          <>
            <FormTextField
              label={t(platform ? 'auth.platform.identifier' : 'auth.identifier')}
              placeholder={t(platform ? 'auth.platform.identifierPlaceholder' : 'auth.identifierPlaceholder')}
              autoComplete="username"
              autoFocus={!mobile}
              height={h}
              value={identifier}
              onChange={(e) => {
                setIdentifier(e.target.value);
                setMissing(false);
              }}
              error={missing ? t('auth.errors.identifierRequired') : undefined}
            />
            <Button
              type="submit"
              disabled={sending}
              startIcon={sending ? <Spinner /> : undefined}
              sx={mobile ? { height: 52, fontSize: 16 } : { height: 48, fontSize: 15 }}
            >
              {t('auth.forgotSubmit')}
            </Button>
          </>
        )}
        <Button
          variant="text"
          onClick={onBack}
          startIcon={<Icon name="arrow_back" size={18} mirrorInRtl />}
          sx={{ alignSelf: 'center', minHeight: 44 }}
        >
          {t('auth.reset.backToLogin')}
        </Button>
      </Box>
    </AuthLayout>
  );
}
